import Vue from "vue";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import axios from "@/plugins/http";

import { API_ROUTES } from "@/utils/API";
import { SMUser } from "@/store";
import { API_TCheck } from "@/models/document_checks";
import { API_TDocumentCard, API_TFile } from "@/models/document_cards";

export type TCosDateResponse = {
  date: string;
};

export const createFileLink = (binary: ArrayBuffer, type = "application/pdf") => {
  const blob = new Blob([binary], { type });
  const link = URL.createObjectURL(blob);

  return link;
};

@Module({ name: "MDocuments", namespaced: true })
export default class MDocuments extends VuexModule {
  documentId: number | string | null = null;

  documentCard: API_TDocumentCard | null = null;

  caseId: number | null = null;

  companyId: number | null = null;

  checks: API_TCheck[] = [];

  get isServices() {
    return SMUser.isCandidate || SMUser.isExpert;
  }

  get documentCardTitle() {
    return this.documentCard?.document_template.name || "";
  }

  selectedChecks: { [key: number]: string } = {};

  get uploadedFiles() {
    if (!this.documentCard) return [];

    return this.documentCard.files;
  }

  get activeUploadedFiles() {
    return this.uploadedFiles.filter(({ status }) => status !== "archived");
  }

  get archivedUploadedFiles() {
    return this.uploadedFiles.filter(({ status }) => status === "archived");
  }

  @Mutation
  setDocumentId(id: number | string) {
    this.documentId = id;
  }

  @Mutation
  unsetDocumentId() {
    this.documentId = null;
  }

  @Mutation
  setDocumentCard(payload: API_TDocumentCard) {
    this.documentCard = payload;
  }

  @Mutation
  setDocumentCardStatus(payload: string) {
    this.documentCard.status = payload;
  }

  @Mutation
  unsetDocumentCard() {
    this.documentCard = null;
    this.selectedChecks = {};
  }

  @Mutation
  setCaseId(id: number) {
    this.caseId = id;
  }

  @Mutation
  unsetCaseId() {
    this.caseId = null;
  }

  @Mutation
  setCompanyId(id: number) {
    this.companyId = id;
  }

  @Mutation
  unsetCompanyId() {
    this.companyId = null;
  }

  @Mutation
  initChecks() {
    if (!this.documentCard) return;

    this.checks = this.documentCard.document_template.checks;
  }

  @Mutation
  unsetChecks() {
    this.checks = [];
  }

  @Mutation
  setChecks(payload: API_TCheck[]) {
    this.checks = payload;
  }

  @Mutation
  selectCheck({ id, value }: { id: number; value: string }) {
    // eslint-disable-next-line import/no-named-as-default-member
    Vue.set(this.selectedChecks, id, value);
  }

  @Mutation
  initSelectedChecks() {
    if (!this.documentCard) return (this.selectedChecks = {});

    const { values } = this.documentCard;

    try {
      const parsedValues = JSON.parse(values);

      this.selectedChecks = parsedValues;
    } catch {
      this.selectedChecks = {};
    }
  }

  @Mutation
  unsetSelectedChecks() {
    this.selectedChecks = {};
  }

  @Action
  async fetchDocumentCard() {
    if (!this.documentId) return null;

    try {
      let url = API_ROUTES.DOCUMENT(this.documentId);

      if (this.companyId)
        url = API_ROUTES.EXPERT_COMPANIES_DOCUMENT(this.companyId, this.documentId);

      if (this.companyId && this.caseId)
        url = API_ROUTES.EXPERT_COMPANIES_EMPLOYEES_DOCUMENT(
          this.companyId,
          this.caseId,
          this.documentId
        );

      if (this.isServices && this.caseId && !this.companyId)
        url = API_ROUTES.CASE_DOCUMENT(this.caseId, this.documentId);

      const { data } = await axios.get<API_TDocumentCard>(url);

      return data;
    } catch {
      return null;
    }
  }

  @Action
  async updateChecks() {
    if (!this.documentCard) return false;

    const url =
      this.isServices && this.caseId
        ? API_ROUTES.CASE_DOCUMENT(this.caseId, this.documentCard.ID)
        : API_ROUTES.DOCUMENT(this.documentCard.ID);

    const values = JSON.stringify(this.selectedChecks);

    try {
      await axios.put(url, { values });

      return true;
    } catch {
      return false;
    }
  }

  @Action
  async fetchCosDate() {
    if (!this.documentId) return { date: "" } as TCosDateResponse;

    try {
      const url = API_ROUTES.COS_DATE_DOCUMENT(this.documentId);
      const { data } = await axios.get<TCosDateResponse>(url);
      return data;
    } catch {
      return { date: "" } as TCosDateResponse;
    }
  }

  routeFullPath = "";

  @Mutation
  setRouteFullPath(path: string) {
    this.routeFullPath = path;
  }

  @Mutation
  unsetRouteFullPath() {
    this.routeFullPath = "";
  }

  @Action
  async getFileUrl(file?: API_TFile): Promise<string> {
    if (!file) return API_ROUTES.DOCUMENT_EXAMPLE(this.documentCard.ID);

    // TODO: setRouteFullPath, isDocumentFile: replace hotfix after new endpoint is done
    const isDocumentFile = this.routeFullPath.includes("employees") && SMUser.isExpert;

    if (isDocumentFile) return API_ROUTES.DOCUMENT_FILE(this.documentCard.ID, file.ID);

    if (this.isServices && this.caseId)
      return API_ROUTES.CASE_DOCUMENT_FILE(this.caseId, this.documentCard.ID, file.ID);

    return API_ROUTES.DOCUMENT_FILE(this.documentCard.ID, file.ID);
  }

  @Action
  async getFileData(file?: API_TFile) {
    if (!this.documentCard) return null;

    try {
      const url = await this.getFileUrl(file);

      const response = await axios.get<ArrayBuffer>(url, {
        responseType: "arraybuffer",
      });

      const { data } = response;

      if (!data) return;

      const contentDisposition = response.headers["content-disposition"];

      const link = createFileLink(data);

      let name = await this.getFileNameContentDisposition(contentDisposition);

      if (!name) name = file ? file.name : `${this.documentCardTitle} example`;

      return { link, name };
    } catch {
      return null;
    }
  }

  @Action
  async getFileNameContentDisposition(headerString: string) {
    if (!headerString) {
      console.log("content-disposition not found");

      return "";
    }

    const match = /filename=([^;]+)/.exec(headerString);

    if (match && match[1]) {
      const filename = match[1].trim();

      return filename;
    } else {
      console.log("Filename not found in content-disposition");

      return "";
    }
  }

  @Action
  downloadFile({ link, name }: { link: string; name: string }) {
    const a = document.createElement("a");
    a.href = link;
    a.download = name;
    a.click();
    a.remove();
    URL.revokeObjectURL(link);
  }

  @Action
  async deleteFile(file: API_TFile) {
    if (!this.documentCard) return false;

    const url =
      this.isServices && this.caseId
        ? API_ROUTES.CASE_DOCUMENT_FILE(this.caseId, this.documentCard.ID, file.ID)
        : API_ROUTES.DOCUMENT_FILE(this.documentCard.ID, file.ID);
    try {
      await axios.delete(url);

      return true;
    } catch {
      return false;
    }
  }
}
